import { Property } from 'csstype';
import { useEffect } from 'react';

import { XmarkIcon } from '../../index';
import { addStyles } from '../../utils/helpers';

export interface DrawerProps {
  /** Controls the open state of the drawer */
  open: boolean;
  /** Callback when the drawer is closed */
  closeDrawer: () => void;
  /** Show overlay behind drawer **/
  overlay?: boolean;
  /** The placement of the drawer **/
  placement?: keyof typeof options.placement;
  /** The header of the drawer **/
  header: string;
  /** The body of the drawer **/
  body: React.ReactNode;
  /** The footer of the drawer **/
  footer?: React.ReactNode;
  /** Show close button in header **/
  showHeaderClose?: boolean;
  /** Control width **/
  width?: Property.Width;
}

const options = {
  baseOverlay: 'z-30 fixed inset-0',
  baseDrawer:
    'fixed flex flex-col bg-white bg-clip-padding shadow-lg outline-none text-gray-900 border-none z-40 divide-y divide-gray-300 justify-between transition-all duration-300 delay-100',
  placement: {
    right: 'top-0 bottom-0 right-0 md:rounded-l-lg',
    left: 'top-0 bottom-0 left-0 md:rounded-r-lg',
    top: 'top-0 left-0 right-0 h-2/3 md:rounded-b-lg',
    bottom: 'bottom-0 left-0 right-0 h-2/3 md:rounded-t-lg',
  },
  hide: {
    left: '-translate-x-full shadow-none',
    right: 'translate-x-full shadow-none',
    top: '-translate-y-full shadow-none',
    bottom: 'translate-y-full shadow-none',
  },
  show: {
    left: 'translate-x-0',
    right: 'translate-x-0',
    top: 'translate-y-0',
    bottom: 'translate-y-0',
  },
  dimensions: {
    width: 'w-full md:w-96',
  },
};

export function Drawer({ open = false, overlay = true, placement = 'right', ...props }: DrawerProps) {
  const { closeDrawer, header, body, footer, showHeaderClose = true, width } = props;

  useEffect(() => {
    const keyPressed = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        closeDrawer();
      }
    };

    window.addEventListener('keydown', keyPressed);
    return () => window.removeEventListener('keydown', keyPressed);
  }, [open, closeDrawer]);

  return (
    <div className="drawer-wrapper" role="dialog" aria-hidden={!open} aria-label={header}>
      {overlay && (
        <div
          role="presentation"
          className={addStyles([options.baseOverlay, open ? 'translate-x-0' : 'translate-x-full'])}
          onClick={closeDrawer}
        >
          <div className="absolute inset-0 bg-black opacity-50" />
        </div>
      )}
      <div
        className={addStyles([
          options.baseDrawer,
          options.placement[placement],
          open ? options.show[placement] : options.hide[placement],
          width ? undefined : options.dimensions.width,
        ])}
        style={{ width }}
      >
        <div className="flex items-center justify-between p-4">
          <span className="headline-s">{header}</span>
          {showHeaderClose && (
            <XmarkIcon
              width="2rem"
              height="2rem"
              onClick={closeDrawer}
              className="rounded-lg hover:cursor-pointer hover:bg-gray-200"
            />
          )}
        </div>

        <div className="offcanvas-body grow overflow-y-auto p-4">{open && body}</div>

        {footer && <div className="offcanvas-footer p-4">{footer}</div>}
      </div>
    </div>
  );
}

export default Drawer;
